import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Elements,
  ExpressCheckoutElement,
  useElements,
  PaymentElement,
  useStripe
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  PaymentIntent,
  PaymentIntentResult,
  StripePaymentElementChangeEvent
} from "@stripe/stripe-js";
import { settings as keySettings } from "../../settings";

const stripePromise = loadStripe(keySettings.publishableKey);

type confirmPaymentFunction = () => Promise<{
  handlerType: "card" | "express_checkout";
  paymentIntentResult: PaymentIntentResult;
}>;

interface checkoutElementsProps {
  clientSecret: string;
  error: string | undefined;
  customerDetails: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
  disabledButton: boolean;
  returnUrl: string;
  processPaymentHooks: {
    handlePaymentIntentRetrieval(
      paymentIntent: PaymentIntent | undefined
    ): void;
    startSubmit(): Promise<void>;
    start(): Promise<void>;
    done(
      payload: PaymentIntentResult,
      handlerType: "card" | "express_checkout"
    ): void;
    error(e: unknown): void;
    handleChange(event: StripePaymentElementChangeEvent): void;
  };
  Button: {
    large: {
      id: string;
      component: (handler: {
        handler: () => void;
        disabled: boolean;
      }) => React.ReactElement<Partial<Record<string, unknown>>>;
    };
    small: {
      id: string;
      component: (handler: {
        handler: () => void;
        disabled: boolean;
      }) => JSX.Element;
    };
  };
}
const CheckOutPaymentElements = ({
  clientSecret,
  error,
  disabledButton,
  processPaymentHooks,
  customerDetails,
  returnUrl,
  Button
}: checkoutElementsProps) => {
  const fontPath = `${
    window.location.origin
  }${require("../../fonts/AvenirNextCyr-Regular.ttf")}`;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        fonts: [
          {
            family: "AvenirNextCyr-Medium",
            src: `url(${fontPath})`,
            weight: "500"
          },
          {
            family: "AvenirNextCyr-Medium",
            src: `url(${fontPath})`,
            weight: "400"
          }
        ],
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorBackground: "#ffffff",
            fontFamily:
              'AvenirNextCyr-Medium, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen',
            borderRadius: "6px"
          },
          rules: {
            ".Tab--selected": {
              borderColor: "#3dc5d6",
              boxShadow: "none"
            },
            ".Tab--selected:focus": {
              borderColor: "#3dc5d6",
              boxShadow: "none"
            },
            ".Tab--selected:active": {
              borderColor: "#3dc5d6",
              boxShadow: "none"
            },

            ".Label": {
              fontSize: "15.2px",
              fontWeight: "500",
              lineHeight: "21px",
              color: "#3dc5d6"
            },

            ".Input": {
              backgroundColor: "#f5f5f5",
              borderColor: "#bfc3c7",
              fontSize: "13px"
            }
          }
        }
      }}
    >
      <PaymentElements
        clientSecret={clientSecret}
        error={error}
        customerDetails={customerDetails}
        disabledButton={disabledButton}
        returnUrl={returnUrl}
        processPaymentHooks={processPaymentHooks}
        Button={Button}
      />
    </Elements>
  );
};

const PaymentElements = ({
  clientSecret,
  error,
  disabledButton,
  processPaymentHooks,
  customerDetails,
  returnUrl,
  Button
}: checkoutElementsProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isWalletReady, setIsWalletReady] = useState(false);

  disabledButton = disabledButton || !stripe || !elements || !clientSecret;

  useEffect(() => {
    if (!stripe || !clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      processPaymentHooks.handlePaymentIntentRetrieval(paymentIntent);
    });
  }, [stripe, clientSecret, processPaymentHooks]);

  const processPayment = async (confirm: confirmPaymentFunction) => {
    try {
      await processPaymentHooks.start();

      const { handlerType, paymentIntentResult: payload } = await confirm();

      processPaymentHooks.done(payload, handlerType);
    } catch (e) {
      processPaymentHooks.error(e);
    }
  };

  const handleSubmit = async (handlerType: "card" | "express_checkout") => {
    const confirm: confirmPaymentFunction = async () => {
      if (!stripe || !clientSecret || !elements)
        throw new Error("Something went wrong while processing the payment");

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message || "Payment submission failed");
      }

      await processPaymentHooks.startSubmit();

      const payload = await stripe?.confirmPayment({
        clientSecret,
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              email: customerDetails.email,
              name: `${customerDetails.firstName} ${customerDetails.lastName}`.trim(),
              phone: customerDetails.phone
            }
          }
        },
        redirect: "if_required"
      });

      return {
        handlerType: handlerType,
        paymentIntentResult: payload
      };
    };

    await processPayment(confirm);
  };

  const LargePayButton = useMemo(() => Button.large.component, [Button.large]);
  const SmallPayButton = useMemo(() => Button.small.component, [Button.small]);

  const LargeButtonElement = document.getElementById(Button.large.id);
  const SmallButtonElement = document.getElementById(Button.small.id);

  return (
    <>
      <PaymentElement
        onChange={processPaymentHooks.handleChange}
        options={{
          layout: {
            type: "tabs",
            defaultCollapsed: false
          },
          fields: {
            billingDetails: {
              address: "auto",
              email: "never",
              name: "never",
              phone: "never"
            }
          }
        }}
      />
      {error && <div id="payment-message">{error}</div>}

      {isWalletReady && <span className="payment_separator">OR</span>}
      <ExpressCheckoutElement
        options={{
          layout: {
            maxColumns: 1
          },
          buttonType: {
            googlePay: "pay"
          },
          buttonTheme: {
            applePay: "white-outline",
            googlePay: "white"
          }
        }}
        onReady={(event) => {
          if (event.availablePaymentMethods) {
            setIsWalletReady(true);
          }
        }}
        className="express_checkout"
        onConfirm={() => handleSubmit("express_checkout")}
      />
      {!!LargeButtonElement &&
        createPortal(
          <LargePayButton
            handler={() => handleSubmit("card")}
            disabled={disabledButton}
          />,
          LargeButtonElement
        )}
      {!!SmallButtonElement &&
        createPortal(
          <SmallPayButton
            handler={() => handleSubmit("card")}
            disabled={disabledButton}
          />,
          SmallButtonElement
        )}
    </>
  );
};

export default CheckOutPaymentElements;
